import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

/**
 * D-143 — the browser-scoped request counter shown under the OTP prompt.
 *
 * WHAT IT SAYS, and why the wording is load-bearing. It says "verification
 * requests used today", never "OTPs sent", "emails sent", "account requests today"
 * or "remaining quota" (D-143 rule 3). The number comes from a server-side counter
 * keyed only by an opaque session id, incremented BEFORE the address is looked up,
 * so it is identical for a real account and an unknown address — which is exactly
 * why it can be displayed to an anonymous caller at all. The account's real OTP
 * count would differ between those two cases, so it may only be disclosed inside
 * the OTP email, where only the mailbox owner can read it.
 *
 * WHY THE UNIVERSAL SENTENCE STAYS. The value is still browser-scoped underneath:
 * it tracks the account's usage only while every request came from this browser,
 * and requests made on a phone or before storage was cleared are invisible here.
 * So the screen can read 2 of 5 while the server has already reached the account
 * limit and is silently suppressing. The supporting line therefore admits that a
 * missing code may mean today's limit was reached (D-142 rule 2), rather than
 * promising the next request will be accepted.
 *
 * WHAT IT MUST NEVER SAY (D-144 rule 7). No reset timestamp, no UTC, no IANA zone
 * name, no abbreviation or offset, and no reference to the organisation's timezone.
 * Since D-144 the quota resets on the TENANT'S calendar day, and this component is
 * rendered from a response computed before the tenant is known, so any such claim
 * would be a guess. The authoritative reset time travels in the OTP email.
 *
 * ACCESSIBILITY. Colour is not the only signal (D-143 rule 11): at the ceiling the
 * text changes, a warning glyph appears, and the block is announced through
 * `role="alert"`.
 *
 * The two planes pass their own classes because their palettes differ — staff uses
 * `--destructive`, the customer portal uses `--customer-danger`, which every one of
 * the six skins defines in light and dark. The SEMANTICS are shared here so the two
 * cannot drift apart.
 */
export interface RecoverySessionCounterProps {
  /** Requests made from this browser, as counted by the server. */
  used: number;
  /** The policy ceiling, 5. */
  limit: number;
  /** Classes for the normal (muted) state. */
  mutedClassName: string;
  /** Classes for the RED ceiling state. */
  dangerClassName: string;
  /** Extra classes on the wrapper. */
  className?: string;
  /** DOM id, so a disabled control can point at this explanation. */
  id?: string;
}

export function RecoverySessionCounter({
  used,
  limit,
  mutedClassName,
  dangerClassName,
  className = '',
  id,
}: RecoverySessionCounterProps) {
  const atCeiling = used >= limit;

  return (
    <div
      id={id}
      className={`${className} ${atCeiling ? dangerClassName : mutedClassName}`.trim()}
      // Announced only when it turns into a warning; a routine 2/5 does not
      // deserve to interrupt a screen reader mid-flow.
      role={atCeiling ? 'alert' : undefined}
      aria-live={atCeiling ? 'assertive' : 'polite'}
      data-testid="recovery-session-counter"
      data-at-limit={atCeiling ? 'true' : 'false'}
    >
      {/* D-144 rule 7 — this component renders from a response computed BEFORE
          the account is looked up, so it has no tenant identity and must not
          claim any: no reset timestamp, no UTC, no IANA zone, no offset, and no
          reference to the organisation's timezone. Since D-144 the quota resets
          on the TENANT'S calendar day, which for a non-UTC tenant is not this
          counter's own UTC-aligned window — naming an instant here would be
          confidently wrong. The authoritative reset time is disclosed only in
          the OTP email, which the mailbox owner alone can read. */}
      <p className="font-medium">
        {atCeiling && (
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className="mr-1.5"
            // The glyph is the non-colour signal; the sentence beside it already
            // carries the meaning, so it must not be read out twice.
            aria-hidden="true"
          />
        )}
        {atCeiling
          ? 'Verification request limit reached'
          : `${used} of ${limit} verification requests used today`}
      </p>
      {atCeiling ? (
        <>
          {/* Scoped to THIS SESSION, deliberately. The counter is keyed to an
              opaque session id and cannot see requests made elsewhere, so it may
              not assert that the ACCOUNT has exhausted its quota — it does not
              know that, and saying so would claim knowledge this component is
              built never to have. */}
          <p className="mt-1">No more verification requests can be made from this session today.</p>
          <p className="mt-1">Please try again later.</p>
        </>
      ) : (
        <p className="mt-1">
          One request per minute. If you don&apos;t receive a code, you may have reached
          today&apos;s limit.
        </p>
      )}
    </div>
  );
}
